/**
 * Phase 8.12 — Screen 37: async duel status.
 *
 * States rendered:
 *   - "Your turn"        — you're next to play. Big teal hero.
 *   - "Open · waiting"   — public duel still awaiting a joiner. Cancel.
 *   - "Waiting for them" — you played, they haven't. Prominent card
 *                          with YOUR score front-and-centre, their
 *                          deadline countdown, share button.
 *   - "Result"           — completed / forfeited / tie. Hero win-loss
 *                          banner, both scores, Rematch + Share.
 *
 * The page auto-polls every 8s so a late settle (opponent submits
 * after you) or a forfeit sweeper flip auto-transitions to the
 * result without needing a manual refresh.
 */

import { api, ApiError } from '../api.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';
import { fmtCountdown, rankLabel } from './duels_browse.js';

const POLL_MS = 8_000;
let _pollTimer = null;

export async function renderDuelStatus(root) {
  stopPolling();
  window.addEventListener('popstate', stopPolling, { once: true });

  const duelId = window.location.pathname.match(/^\/duel\/([^/]+)$/)?.[1];
  if (!duelId) {
    navigate('/duels');
    return;
  }
  root.innerHTML = `
    <div class="appbar">
      <a href="/duels" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">1-v-1 duel</div>
    </div>
    <div class="pad" id="status-page"><div class="skel"></div><div class="skel"></div></div>
  `;

  let me;
  try {
    me = await api('/me');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    document.getElementById('status-page').innerHTML =
      `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }

  // First paint + poll loop. Poll survives the initial render so
  // late settle / forfeit flips advance the page in place.
  await fetchAndPaint(duelId, me);
  schedulePoll(duelId, me);
}

function stopPolling() {
  if (_pollTimer) {
    clearTimeout(_pollTimer);
    _pollTimer = null;
  }
}

function schedulePoll(duelId, me) {
  stopPolling();
  _pollTimer = setTimeout(async () => {
    if (!document.getElementById('status-page')) return;
    await fetchAndPaint(duelId, me, { silent: true });
    if (document.getElementById('status-page')) schedulePoll(duelId, me);
  }, POLL_MS);
}

async function fetchAndPaint(duelId, me, { silent = false } = {}) {
  let duel;
  try {
    duel = await api(`/duels/${duelId}`);
  } catch (err) {
    if (silent) return;
    document.getElementById('status-page').innerHTML =
      `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
    return;
  }
  const page = document.getElementById('status-page');
  if (page) paint(page, duel, me);
  // Stop polling once we hit a terminal state.
  if (['completed', 'forfeited', 'expired_void', 'cancelled'].includes(duel.status)) {
    stopPolling();
  }
}

function paint(page, duel, me) {
  const state = computeState(duel, me);
  const category = escape(duel.category_name || 'Trivia');

  const runsById = new Map(duel.runs.map((r) => [r.user_id, r]));
  const myRun = runsById.get(me.id);
  const otherPlayer = duel.opponent && duel.opponent.id === me.id ? duel.creator : duel.opponent;

  let hero;
  switch (state) {
    case 'your-turn':
      hero = renderYourTurn(duel, me, runsById);
      break;
    case 'open-waiting':
      hero = renderWaitingForOpponent(duel);
      break;
    case 'waiting-them':
      hero = renderWaitingForThem(duel, otherPlayer, myRun, me, runsById, 'them');
      break;
    case 'waiting-open':
      // Public duel, creator has played, still open for anyone to join.
      hero = renderWaitingForThem(duel, otherPlayer, myRun, me, runsById, 'anyone');
      break;
    case 'completed':
    case 'forfeited':
    case 'tie':
      hero = renderResult(duel, me, runsById, state);
      break;
    default:
      hero = `<div class="card">This duel is ${escape(duel.status)}.</div>`;
  }

  page.innerHTML = `
    <div class="row" style="margin-bottom:10px">
      <span class="tag teal">${category}</span>
      <div class="sp"></div>
      <span class="tag muted">${escape(stakeLabel(duel))}</span>
    </div>
    ${hero}
  `;

  wireHero(page, duel, me);
}

function computeState(duel, me) {
  if (duel.status === 'completed' && !duel.winner_id) return 'tie';
  if (duel.status === 'completed') return 'completed';
  if (duel.status === 'forfeited') return 'forfeited';
  if (duel.status === 'expired_void' || duel.status === 'cancelled') return duel.status;
  if (duel.status === 'open') {
    if (duel.viewer_role !== 'creator') return 'observer';
    // Public duel, creator here. If the creator has already played
    // their run, show the partial result view (same as `waiting-them`
    // but with an "anyone can still join" chip + Cancel button).
    // If they haven't played yet, keep the compact "waiting for an
    // opponent · Cancel" card.
    const myRun = duel.runs.find((r) => r.user_id === me.id);
    if (myRun?.status === 'submitted') return 'waiting-open';
    return 'open-waiting';
  }
  // `matched` means both sides are known but the creator's run is
  // still in progress. If the viewer is the opponent, it's NOT their
  // turn yet — they're waiting for the creator to finish. Once the
  // creator submits, the backend flips status → awaiting_opponent.
  if (duel.status === 'matched' && duel.viewer_role === 'opponent') {
    return 'waiting-them';
  }
  const myRun = duel.runs.find((r) => r.user_id === me.id);
  if (!myRun || myRun.status === 'in_progress') return 'your-turn';
  return 'waiting-them';
}

function stakeLabel(duel) {
  return duel.stake_kobo > 0 ? formatKobo(duel.stake_kobo) + ' stake' : 'Free duel';
}

function payoutLabel(duel) {
  if (duel.stake_kobo <= 0) return '';
  const pool = duel.stake_kobo * 2;
  const rake = Math.floor((pool * (duel.rake_bps || 1000)) / 10_000);
  return `winner takes ${formatKobo(pool - rake)}`;
}

function winnerPayoutKobo(duel) {
  const pool = duel.stake_kobo * 2;
  const rake = Math.floor((pool * (duel.rake_bps || 1000)) / 10_000);
  return pool - rake;
}

// ---- state renderers ------------------------------------------------------

function renderYourTurn(duel, me, runsById) {
  const otherId = duel.creator?.id === me.id ? duel.opponent?.id : duel.creator?.id;
  const other = duel.creator?.id === me.id ? duel.opponent : duel.creator;
  const otherRun = otherId ? runsById.get(otherId) : null;
  return `
    <div class="card" style="border-color:#1E6E5C;background:linear-gradient(135deg,#15323A,#12232C);padding:14px">
      <div class="center">
        <span class="tag" style="background:#0F352A;color:#5FE0BE;border:1px solid #1E6E5C">
          YOUR TURN · ${escape(fmtCountdown(duel.turn_deadline_at))}
        </span>
      </div>
      <div class="row" style="margin-top:12px;gap:10px">
        ${playerColumn(other, otherRun, false)}
        <div style="font-size:20px;color:var(--mute);font-weight:700;align-self:center">VS</div>
        ${playerColumn(me, null, true)}
      </div>
      <div style="font-size:11px;color:#9FE9D2;font-weight:500;text-align:center;margin-top:10px">
        Same ${duel.question_count} questions · ${escape(stakeLabel(duel))} · ${escape(payoutLabel(duel))}
      </div>
      <button class="btn teal" data-play-id="${escape(duel.id)}" style="margin-top:12px">Play my round now</button>
    </div>
  `;
}

function renderWaitingForOpponent(duel) {
  return `
    <div class="card" style="padding:14px">
      <div class="row">
        <div class="ic" style="background:#1E2A44;color:#7FB4FF">🌐</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600">Waiting for an opponent</b>
          <div style="font-size:11px;color:var(--mute);font-weight:500">Public · ${escape(duel.category_name || 'Trivia')} ·
            ${escape(stakeLabel(duel))} · expires in ${escape(fmtCountdown(duel.publish_expires_at))}</div>
        </div>
        <button class="btn ghost sm" data-cancel-id="${escape(duel.id)}"
                style="margin:0;color:#FF8FB6;border-color:#5A2440">Cancel</button>
      </div>
    </div>
  `;
}

function renderWaitingForThem(duel, otherPlayer, myRun, me, runsById, waitingFor = 'them') {
  // "You've played, now waiting" — same shape as the final result
  // screen (hero + reward cards + standings + Q breakdown) with the
  // outcome unknown and opponent-side details deferred. Auto-poll
  // flips this to `renderResult` the moment the opponent submits.
  //
  // `waitingFor`:
  //   'them'   → opponent is known, deadline is set. Chip: "Waiting
  //              for {name} · {deadline}".
  //   'anyone' → public duel, still open. Chip: "Waiting for anyone
  //              to join · expires in X". Cancel button is offered
  //              (the duel is still cancellable while open).
  const isOpen = waitingFor === 'anyone';
  const myScore = myRun?.score ?? 0;
  const shareable = duel.stake_kobo > 0
    ? `I just scored ${myScore} on a ${escape(duel.category_name || 'trivia')} duel — beat me if you can!`
    : `I just scored ${myScore} on a ${escape(duel.category_name || 'trivia')} duel!`;
  const waitingChip = isOpen
    ? `⏱ Waiting for anyone to join · expires in ${escape(fmtCountdown(duel.publish_expires_at))}`
    : `⏱ Waiting for ${escape(otherPlayer?.username || 'them')} · ${escape(fmtCountdown(duel.turn_deadline_at))}`;
  const cancelButton = isOpen
    ? `<button class="btn ghost" style="flex:1;color:#FF8FB6;border-color:#5A2440"
              data-cancel-id="${escape(duel.id)}">Cancel duel</button>`
    : '';
  return `
    <!-- Waiting hero — same shape as the .hero.dark result banner. -->
    <div class="hero dark center flush" style="padding:30px 18px 22px;">
      <div style="font-family:var(--font-display);font-size:14px;letter-spacing:2px;opacity:0.85;">
        YOU'VE PLAYED
      </div>
      <div style="font-family:var(--font-display);font-size:38px;font-weight:700;color:#fff;margin-top:6px;">
        ${myScore} pts
      </div>
      <div style="font-size:12px;font-weight:500;opacity:0.9;margin-top:2px;">
        ${myRun?.correct ?? 0} / ${duel.question_count} correct
      </div>
      <div class="row" style="justify-content:center;gap:7px;margin-top:14px;flex-wrap:wrap;">
        <span class="tag" style="background:rgba(247,198,103,0.18);color:#F7C667;">
          ${waitingChip}
        </span>
      </div>
    </div>

    ${renderRewardCards()}

    <h3 class="sec">Standings so far</h3>
    ${renderWaitingStandings(duel, me, runsById, otherPlayer, isOpen)}

    ${duel.breakdown && duel.breakdown.length ? `
      <details open>
        <summary style="cursor:pointer;color:var(--ink-2);font-size:13px;font-weight:600;margin:12px 0;">
          Your question breakdown
        </summary>
        ${renderBreakdown(duel, me)}
      </details>
    ` : ''}

    <div class="row" style="gap:9px;margin-top:14px;">
      ${cancelButton}
      <button class="btn ghost" style="flex:1;"
              data-share='${escape(JSON.stringify({ text: shareable, url: shareUrl(duel) }))}'>
        📣 Share your score
      </button>
    </div>

    <div class="cta-row" style="margin-top:8px;">
      <a href="/" data-link class="btn ghost">Home</a>
    </div>
  `;
}

function renderWaitingStandings(duel, me, runsById, otherPlayer, isOpen = false) {
  // Same shape as `renderStandings` but the opponent's row shows a
  // waiting / not-yet-joined state instead of a score.
  const myRun = runsById.get(me.id);
  const otherId = duel.creator?.id === me.id ? duel.opponent?.id : duel.creator?.id;
  const otherRun = otherId ? runsById.get(otherId) : null;
  const myInitial = escape((me.username || me.id || 'Y').slice(0, 1).toUpperCase());
  const otherInitial = isOpen
    ? '?'
    : escape((otherPlayer?.username || otherPlayer?.id || '?').slice(0, 1).toUpperCase());
  const otherName = isOpen
    ? 'Anyone can join'
    : escape(otherPlayer?.username || 'Opponent');
  const otherStatus = isOpen
    ? 'no one has joined yet'
    : otherRun?.status === 'in_progress'
      ? 'playing…'
      : (otherRun ? 'ready' : 'not started');
  return `
    <div class="card" style="padding:11px;border-color:var(--violet);">
      <div class="row">
        <div class="ic gold" style="color:#3a2a00;font-weight:700;">1</div>
        <div class="av sm">${myInitial}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">You</b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">
            ${myRun?.correct ?? 0} correct · ${((myRun?.total_ms ?? 0) / 1000).toFixed(1)}s
          </div>
        </div>
        <b style="font-family:var(--font-display);color:#fff;font-weight:600;">${myRun?.score ?? 0} pts</b>
      </div>
    </div>
    <div class="card" style="padding:11px;">
      <div class="row">
        <div class="ic">?</div>
        <div class="av sm">${otherInitial}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">
            ${otherName}
          </b>
          <div style="font-size:11px;color:var(--mute);font-weight:500;">
            ${escape(otherStatus)}
          </div>
        </div>
        <b style="font-family:var(--font-display);color:var(--mute);font-weight:600;">— pts</b>
      </div>
    </div>
  `;
}

function renderResult(duel, me, runsById, state) {
  const myRun = runsById.get(me.id);
  const otherId = duel.creator?.id === me.id ? duel.opponent?.id : duel.creator?.id;
  const otherRun = otherId ? runsById.get(otherId) : null;
  const otherPlayer = duel.creator?.id === me.id ? duel.opponent : duel.creator;
  const myScore = myRun?.score ?? 0;
  const otherScore = otherRun?.score ?? 0;

  const isWin = duel.winner_id === me.id;
  const isTie = state === 'tie';
  const isForfeitWin = isWin && state === 'forfeited';
  const isForfeitLoss = !isWin && !isTie && state === 'forfeited';
  const isPaid = duel.stake_kobo > 0;

  // ---- Hero banner — same shape as match_result.js:renderHero ----
  //   violet tone for wins/forfeit-wins, dark for everything else
  //   38px display-font headline: payout on paid wins, score line on
  //   losses/free/ties, "credited to your wallet instantly" subtitle
  //   on paid wins.
  let heroTone;
  let heroLabel;
  let heroBigHtml;
  let heroSubtitle;

  if (isTie) {
    heroTone = 'dark';
    heroLabel = 'DRAW';
    heroBigHtml = `${myScore} pts`;
    heroSubtitle = 'Both stakes refunded.';
  } else if (isForfeitWin) {
    heroTone = 'violet';
    heroLabel = 'WON BY FORFEIT';
    heroBigHtml = isPaid ? `+ ${escape(formatKobo(winnerPayoutKobo(duel)))}` : `${myScore} pts`;
    heroSubtitle = isPaid
      ? 'credited to your wallet instantly'
      : `${escape(otherPlayer?.username || 'They')} didn't play in time.`;
  } else if (isWin) {
    heroTone = 'violet';
    heroLabel = 'YOU WON';
    heroBigHtml = isPaid ? `+ ${escape(formatKobo(winnerPayoutKobo(duel)))}` : `${myScore} pts`;
    heroSubtitle = isPaid
      ? 'credited to your wallet instantly'
      : `${myRun?.correct ?? 0} / ${duel.question_count} correct`;
  } else if (isForfeitLoss) {
    heroTone = 'dark';
    heroLabel = 'FORFEIT';
    heroBigHtml = '—';
    heroSubtitle = "You didn't play in time. Pot went to your opponent.";
  } else {
    heroTone = 'dark';
    heroLabel = 'YOU LOST';
    heroBigHtml = `${myScore} pts`;
    heroSubtitle = `${myRun?.correct ?? 0} / ${duel.question_count} correct`;
  }

  const heroClass = heroTone === 'violet' ? 'hero violet' : 'hero dark';

  // Share copy — win phrasing on wins, honest loss phrasing otherwise.
  const shareText = isWin
    ? `I just won a ${duel.category_name || 'trivia'} duel ${myScore}–${otherScore} on Triviana!`
    : isTie
      ? `We tied ${myScore}–${otherScore} on a Triviana duel — think you can beat us both?`
      : `Just played a Triviana duel — ${myScore}–${otherScore}. Come try me!`;

  return `
    <div class="${heroClass} center flush" style="padding:30px 18px 22px;">
      <div style="font-family:var(--font-display);font-size:14px;letter-spacing:2px;opacity:0.85;">
        ${escape(heroLabel)}
      </div>
      <div style="font-family:var(--font-display);font-size:38px;font-weight:700;color:#fff;margin-top:6px;">
        ${heroBigHtml}
      </div>
      <div style="font-size:12px;font-weight:500;opacity:0.9;margin-top:2px;">
        ${heroSubtitle}
      </div>
    </div>

    ${renderRewardCards()}

    ${isPaid ? renderMoney(duel, isWin, isTie) : ''}

    <h3 class="sec">Final standings</h3>
    ${renderStandings(duel, me, runsById)}

    ${duel.breakdown && duel.breakdown.length ? `
      <details>
        <summary style="cursor:pointer;color:var(--ink-2);font-size:13px;font-weight:600;margin:12px 0;">
          Question breakdown
        </summary>
        ${renderBreakdown(duel, me)}
      </details>
    ` : ''}

    <div class="row" style="gap:9px;margin-top:14px;">
      <button class="btn teal" data-rematch-id="${escape(duel.id)}" style="flex:1;">Rematch</button>
      ${isWin
        ? `<button class="btn ghost" style="flex:1;"
             data-share='${escape(JSON.stringify({ text: shareText, url: shareUrl(duel) }))}'>
             📣 Share win
           </button>`
        : `<button class="btn ghost" style="flex:1;"
             data-share='${escape(JSON.stringify({ text: shareText, url: shareUrl(duel) }))}'>
             📣 Share
           </button>`}
    </div>

    <div class="cta-row" style="margin-top:8px;">
      <a href="/wallet" data-link class="btn ghost">Open wallet</a>
      <a href="/" data-link class="btn ghost">Home</a>
    </div>
  `;
}

// ---- classic-style sub-renders ------------------------------------------

function renderRewardCards() {
  // Phase 11 placeholder — XP/Gems/Streak numbers land here once
  // the progression system ships. Same "+0 / +0 / 1" defaults the
  // classic match_result screen has been showing for months, so
  // the visual weight of the result screen stays consistent.
  return `
    <div class="row" style="gap:9px;">
      ${rewardCard('+0', 'XP', 'var(--gold-t)')}
      ${rewardCard('+0', 'Gems', 'var(--blue-t)')}
      ${rewardCard('1', 'Streak', 'var(--flame)')}
    </div>
  `;
}

function rewardCard(value, label, color) {
  return `
    <div class="card center" style="flex:1;margin:0;padding:12px;">
      <div style="font-family:var(--font-display);font-size:17px;font-weight:600;color:${color};">
        ${escape(value)}
      </div>
      <div style="font-size:10px;color:var(--mute);font-weight:600;text-transform:uppercase;letter-spacing:0.4px;margin-top:2px;">
        ${escape(label)}
      </div>
    </div>
  `;
}

function renderMoney(duel, isWin, isTie) {
  const money = duel.money || {};
  const pool = money.pool_kobo ?? duel.stake_kobo * 2;
  const rake = money.rake_kobo ?? Math.floor((pool * (duel.rake_bps || 1000)) / 10_000);
  const payout = money.payout_kobo ?? (pool - rake);
  const note = isTie
    ? '<div class="note teal">Stake refunded — no rake taken.</div>'
    : '';
  return `
    <h3 class="sec">Money</h3>
    <div class="card">
      <div class="row">
        <div class="stat">
          <div class="v">${escape(formatKobo(pool))}</div>
          <div class="k">Pool</div>
        </div>
        <div class="stat">
          <div class="v">${escape(formatKobo(rake))}</div>
          <div class="k">Rake</div>
        </div>
        <div class="stat">
          <div class="v" style="color:var(--teal-t);">${escape(formatKobo(payout))}</div>
          <div class="k">${isWin ? 'You won' : 'Paid out'}</div>
        </div>
      </div>
      ${note}
    </div>
  `;
}

function renderStandings(duel, me, runsById) {
  // Both players, sorted best score first (tie-break: lower total_ms).
  const players = [];
  for (const run of runsById.values()) {
    const isMe = run.user_id === me.id;
    const cardPlayer = isMe
      ? { id: me.id, username: 'You' }
      : (duel.creator?.id === run.user_id ? duel.creator : duel.opponent) || { id: run.user_id, username: 'Player' };
    players.push({ run, cardPlayer, isMe });
  }
  players.sort(
    (a, b) => b.run.score - a.run.score || a.run.total_ms - b.run.total_ms,
  );
  return players
    .map(({ run, cardPlayer, isMe }, i) => {
      const rank = i + 1;
      const payoutTag = isMe && duel.winner_id === me.id && duel.money && duel.money.payout_kobo > 0
        ? `<span class="tag gold" style="margin-left:9px;">+${escape(formatKobo(duel.money.payout_kobo))}</span>`
        : '';
      const name = isMe ? 'You' : escape(cardPlayer.username || 'Player');
      const initial = escape((cardPlayer.username || cardPlayer.id || '?').slice(0, 1).toUpperCase());
      return `
        <div class="card" style="padding:11px;${isMe ? 'border-color:var(--violet);' : ''}">
          <div class="row">
            <div class="ic ${rank === 1 ? 'gold' : ''}" style="${rank === 1 ? 'color:#3a2a00;font-weight:700;' : ''}">${rank}</div>
            <div class="av sm">${initial}</div>
            <div class="grow">
              <b style="font-size:13px;color:#fff;font-weight:600;">${name}</b>
              <div style="font-size:11px;color:var(--mute);font-weight:500;">
                ${run.correct} correct · ${(run.total_ms / 1000).toFixed(1)}s
                ${run.status === 'forfeited' ? ' · <span style="color:#FF8FB6">forfeited</span>' : ''}
              </div>
            </div>
            <b style="font-family:var(--font-display);color:#fff;font-weight:600;">${run.score} pts</b>
            ${payoutTag}
          </div>
        </div>
      `;
    })
    .join('');
}

function renderBreakdown(duel, me) {
  return duel.breakdown
    .map((q) => renderQuestionRow(q, me, duel))
    .join('');
}

function renderQuestionRow(q, me, duel) {
  const myAnswer = (q.answers || []).find((a) => a.user_id === me.id) || null;
  const otherId = duel.creator?.id === me.id ? duel.opponent?.id : duel.creator?.id;
  const otherAnswer = otherId
    ? (q.answers || []).find((a) => a.user_id === otherId) || null
    : null;
  const correctChoice = q.choices[q.correct_index] ?? '?';
  const image = q.image_url
    ? `<img src="${escape(q.image_url)}" alt="" loading="lazy"
         style="width:100%;max-height:140px;object-fit:cover;border-radius:8px;margin-bottom:8px;" />`
    : '';
  return `
    <div class="card" style="padding:11px;">
      ${image}
      <div style="font-size:12.5px;color:#fff;font-weight:600;line-height:1.4;white-space:pre-wrap;">Q${q.order + 1}. ${escape(q.prompt)}</div>
      <div style="font-size:11px;color:var(--mute);font-weight:500;margin-top:6px;">
        ✓ ${escape(correctChoice)}
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:6px;font-size:11px;font-weight:500;">
        <span style="color:var(--mute);">You</span> ${answerBadge(myAnswer)}
        ${otherAnswer ? `<span style="color:var(--mute);margin-left:6px;">Opp</span> ${answerBadge(otherAnswer)}` : ''}
      </div>
    </div>
  `;
}

// One-line answer badge. Shows the correct/wrong pill + elapsed time,
// then appends "· ½ +N pts" when the player spent a 50/50 on THIS
// question — that's how the opponent (and the caller) sees at a
// glance both that the power-up was used AND the score it produced.
// The pts value renders both for the caller's own reveal AND for the
// opponent's reveal so the two columns stay symmetric.
function answerBadge(ans) {
  if (!ans) return '<span class="status-pill warn">none</span>';
  const secs = (ans.elapsed_ms / 1000).toFixed(1);
  const pill = ans.is_correct
    ? `<span class="status-pill ok">✓ ${secs}s</span>`
    : `<span class="status-pill bad">✗ ${secs}s</span>`;
  if (!ans.fiftyfifty_applied) return pill;
  // 50/50 was spent — mark it and show the score explicitly so the
  // wrong-with-consolation case is legible ("✗ · ½ +50 pts" reads
  // clearly). Symbol is the half-filled circle from the shared
  // fiftyfifty glyph set, chosen for size at 11px inline.
  const pts = Number(ans.score_awarded || 0);
  return `${pill}<span class="status-pill" style="background:rgba(127,180,255,0.16);color:#7FB4FF;border:1px solid rgba(127,180,255,0.4);font-weight:700;" title="50/50 used"
    aria-label="Used 50/50, scored ${pts} points">◐ +${pts} pts</span>`;
}

function playerColumn(player, run, isMe) {
  const played = run?.status === 'submitted';
  const forfeit = run?.status === 'forfeited';
  const line = played
    ? `<div style="font-size:11px;color:var(--mute);font-weight:600;margin-top:3px">Played · scored ${run.score}</div>`
    : forfeit
      ? `<div style="font-size:11px;color:#FF8FB6;font-weight:600;margin-top:3px">Forfeited</div>`
      : `<div style="font-size:11px;color:#F7C667;font-weight:600;margin-top:3px">${isMe ? 'Your move' : 'Waiting'}</div>`;
  const rank = rankLabel(player || {});
  return `
    <div style="flex:1;text-align:center">
      <div class="av" style="width:52px;height:52px;margin:0 auto;${isMe ? 'border:2px solid #5FE0BE' : ''}"></div>
      <b style="display:block;font-size:13px;color:#fff;font-weight:600;margin-top:6px">${escape(isMe ? 'You' : (player?.username || 'Player'))}</b>
      <div style="font-size:10px;color:#5FE0BE;font-weight:600">${escape(rank)}</div>
      ${line}
    </div>
  `;
}

function shareUrl(duel) {
  // Deep-link back to the duel status page. Anyone tapping the link
  // lands on the same duel; if they're not signed in, the router
  // routes them through onboarding first.
  return `${window.location.origin}/duel/${duel.id}`;
}

function wireHero(page, duel, me) {
  page.querySelectorAll('[data-play-id]').forEach((btn) =>
    btn.addEventListener('click', () => navigate(`/duel/${btn.dataset.playId}/play`)),
  );
  page.querySelectorAll('[data-cancel-id]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      if (!confirm('Cancel this duel? Your stake will be refunded.')) return;
      try {
        await api(`/duels/${btn.dataset.cancelId}/cancel`, { method: 'POST' });
        toast.success('Duel cancelled and refunded.');
        navigate('/duels');
      } catch (err) {
        toast.error(err.message || 'Could not cancel.');
      }
    }),
  );
  page.querySelectorAll('[data-rematch-id]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = 'Sending…';
      try {
        const newDuel = await api(`/duels/${btn.dataset.rematchId}/rematch`, { method: 'POST' });
        toast.success('Rematch created — play your round.');
        navigate(`/duel/${newDuel.id}/play`);
      } catch (err) {
        btn.disabled = false;
        btn.textContent = 'Rematch';
        if (err instanceof ApiError && err.code === 'insufficient_balance') {
          toast.error('Not enough balance for that stake.');
        } else {
          toast.error(err.message || 'Could not create the rematch.');
        }
      }
    }),
  );
  page.querySelectorAll('[data-share]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      let payload;
      try {
        payload = JSON.parse(btn.dataset.share);
      } catch {
        return;
      }
      const { text, url } = payload;
      if (navigator.share) {
        try {
          await navigator.share({ title: 'Triviana', text, url });
          return;
        } catch {
          // User cancelled the OS share sheet; fall through to clipboard.
        }
      }
      try {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success('Link copied to clipboard.');
      } catch {
        toast.error('Could not copy — long-press this URL to share:\n' + url);
      }
    }),
  );
}
